import { clsx } from 'clsx';
import { Modal, Upload } from 'antd';
import { RcFile } from 'antd/lib/upload';
import React, { Dispatch, useState } from 'react';
import { CloseCircleFilled, FilePdfOutlined, LoadingOutlined } from '@ant-design/icons';

import { DealerDocumentProps } from '@types';
import { beforeUpload, showAxiosError } from '@utils';
import { ConfirmDelete, ImageWithAuth, ImageZoom, PdfIcon } from '@components';
import cls from './DealerDocuments.module.css';

const MAX_DOCS = 10;

interface Props {
  documents: DealerDocumentProps[];
  setDocuments: Dispatch<React.SetStateAction<DealerDocumentProps[]>>
  loading: boolean;
}

export const DealerDocuments = ({ documents, setDocuments, loading }: Props) => {
  const [previewFile, setPreviewFile] = useState<DealerDocumentProps | null>(null);

  const getBase64 = (file: RcFile): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });

  const onUpload = async (rcFile: RcFile) => {
    try {
      const preview = await getBase64(rcFile);
      const newDoc: DealerDocumentProps = {
        idx: rcFile.uid,
        fileId: null,
        file: rcFile,
        originalName: rcFile.name,
        isPdf: rcFile.type === 'application/pdf',
        preview,
        adminUrl: '',
      };
      setDocuments((prev: DealerDocumentProps[]) => {
        if (prev.length === MAX_DOCS) return prev;
        else return [...prev, newDoc];
      });

      return rcFile.uid;
    } catch (err: any) {
      showAxiosError(err);

      return '';
    }
  };

  const onDelete = (id: string) => {
    setDocuments(prev => prev.filter((d) => d.idx !== id));
  };

  return (
    <div className={cls.wrap}>
      <div className={cls.row}>
        <div className={clsx(cls.col, cls.uploadBlock, documents.length === 10 && cls.hidden)}>
          <Upload
            listType="picture-card"
            showUploadList={false}
            beforeUpload={beforeUpload}
            multiple
            maxCount={MAX_DOCS - documents.length}
            disabled={loading}
            className={cls.upload}
            customRequest={async ({ onSuccess }) => setTimeout(() => {
              onSuccess && onSuccess('ok');
            }, 0)}
            action={onUpload}
            onPreview={() => {}}
          >
            {documents.length >= MAX_DOCS ? null : <div className={cls.uploadBtn}>
              {loading
                ? <LoadingOutlined className={cls.uploadIcon} />
                : <span className={cls.uploadIcon}>+</span>}
            </div>}
          </Upload>
        </div>
        <div className={clsx(cls.col, cls.previews, documents.length === 10 && cls.full)}>
          {documents.length > 0 ? documents.map((f: DealerDocumentProps) =>
            <div className={cls.file} key={f.idx}>
              {f.isPdf ? (f.adminUrl ? (
                <PdfIcon
                  url={f.adminUrl}
                  className={cls.pdfIcon}
                  downloadFilename={`document-${f.idx}.pdf`}
                  downloadLinkClassName={cls.downloadLinkClassName}
                />
              ) : (
                <FilePdfOutlined className={cls.pdfIcon} />
              )
              ) : (
                f.adminUrl ? (
                  <ImageZoom>
                    <ImageWithAuth
                      url={f.adminUrl}
                      className={cls.adminFileImg}
                      wrapClassName={cls.adminFile}
                      width={92}
                      height={92}
                    />
                  </ImageZoom>
                ) : (
                  <img src={f.preview} alt="preview" className={cls.img} onClick={() => setPreviewFile(f)} />
                )
              )}
              <ConfirmDelete title="Are you sure?" cb={() => onDelete(f.idx)} disabled={loading}>
                <CloseCircleFilled className={cls.close} />
              </ConfirmDelete>
            </div>,
          ) : null}
        </div>
      </div>

      <Modal
        centered
        open={!!previewFile}
        title={null}
        footer={null}
        onCancel={() => setPreviewFile(null)}>
        <img src={previewFile?.preview} alt="preview" className={cls.imgModal} />
      </Modal>
    </div>
  );
};
